import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import { getAllHealthHacks, HealthHack } from '../services/api';
import NewsletterSignup from '../components/NewsletterSignup';
import { useScrollToTop } from '../hooks/useScrollToTop';

const TagPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const [hacks, setHacks] = useState<HealthHack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when tag changes
  useScrollToTop();

  useEffect(() => {
    const fetchTagHacks = async () => {
      if (!tag) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const allHacks = await getAllHealthHacks();
        const tagHacks = allHacks.filter(hack => 
          hack.tags && hack.tags.some(t => 
            t.toLowerCase().includes(tag.toLowerCase())
          )
        );
        setHacks(tagHacks);
      } catch (err) {
        setError('Failed to load tag daily tips');
        console.error('Error fetching tag daily tips:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTagHacks();
  }, [tag]);

  const getCategoryName = (categoryId: string) => {
    const categories = {
      'stress-relief': 'Stress Relief',
      mindfulness: 'Mindfulness', 
      sleep: 'Sleep Better',
      'self-care': 'Self Care',
      wellness: 'Wellness'
    };
    return categories[categoryId as keyof typeof categories] || categoryId;
  };

  const getCategoryColor = (category: string) => {
    // Use consistent blue for all categories
    return 'category-badge';
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... - Destress Daily</title>
        </Helmet>
        
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading daily tips...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error - Destress Daily</title>
        </Helmet>
        
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Tag</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <Link to="/tips" className="btn-primary">
              Browse All Daily Tips
            </Link>
          </div>
        </div>
      </>
    );
  }

  const tagName = tag ? decodeURIComponent(tag) : '';

  return (
    <>
      <Helmet>
        <title>Daily Tips Tagged "{tagName}" - Destress Daily</title>
        <meta name="description" content={`Discover daily tips and wellness advice tagged with "${tagName}". Find practical advice for your health journey.`} />
        <meta name="keywords" content={`${tagName}, wellness tips, wellness, stress relief, mindfulness, self care, sleep`} />
        <meta property="og:title" content={`Daily Tips Tagged "${tagName}" - Destress Daily`} />
        <meta property="og:description" content={`Discover daily tips tagged with "${tagName}".`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dailyhealthhacks.com/tag/${encodeURIComponent(tagName)}`} />
        <link rel="canonical" href={`https://dailyhealthhacks.com/tag/${encodeURIComponent(tagName)}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-health-50 via-white to-primary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Daily Tips Tagged "{tagName}"
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover practical wellness tips and advice related to {tagName}.
            </p>
          </div>
        </div>
      </section>

      {/* Daily Tips Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Daily Tips Tagged "{tagName}"
            </h2>
            <p className="text-lg text-gray-600">
              {hacks.length} daily tip{hacks.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Daily Tips Grid */}
          {hacks.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">No Daily Tips Found</h3>
              <p className="text-gray-600 mb-8">
                No daily tips found with the tag "{tagName}". Try browsing all daily tips or check out our categories.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/tips" className="btn-primary">
                  Browse All Daily Tips
                </Link>
                <Link to="/categories" className="btn-secondary">
                  Explore Categories
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {hacks.map((hack) => (
                <article key={hack.id} className="health-hack-card group">
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(hack.category)}`}>
                      {getCategoryName(hack.category)}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                    <Link to={`/tip/${hack.slug}`}>
                      {hack.title}
                    </Link>
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {hack.excerpt}
                  </p>
                  
                  {/* Tags */}
                  {hack.tags && hack.tags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {hack.tags.map((tag, index) => (
                          <Link
                            key={index}
                            to={`/tag/${encodeURIComponent(tag)}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200"
                          >
                            #{tag}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {hack.read_time} min read
                    </div>
                    <Link
                      to={`/tip/${hack.slug}`}
                      className="text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200"
                    >
                      Read More →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSignup variant="hero" />
    </>
  );
};

export default TagPage; 